import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import {
  EmptyState,
  ListCard,
  SkeletonList,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import { tpsCommissionsApi, type TpsCommissionsSummary } from "../api/tpsCommissions";

// TPS Servicing Portal PR-G — the firm's "Commissions" tab: earned servicing-fee
// income accumulating in the Commissions account. Read-only roll-up: a headline
// total plus the per-distribution line items. No React Query in the portal; load
// is plain useState.

const STATUS_TONE: Record<string, StatusTone> = {
  Pending: "warn",
  Paid: "ok",
  Reversed: "danger",
};

function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? "neutral";
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** "2026-06-01" → "June 2026". */
function periodLabel(periodStart: string): string {
  const d = new Date(`${periodStart}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

export function TpsCommissionsTab() {
  const [summary, setSummary] = useState<TpsCommissionsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsCommissionsApi.list();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your commissions."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!summary) {
    return <SkeletonList rows={4} data-testid="tps-commissions-loading" />;
  }

  return (
    <section aria-label="Commissions" className="flex flex-col gap-space-4">
      <div
        className="rounded-lg border border-border-light bg-white-pure p-space-4 shadow-sm"
        data-testid="tps-commissions-total"
      >
        <div className="text-caption uppercase tracking-label text-gray-mid">Total earned</div>
        <div className="mt-space-1 font-display text-h2 font-bold text-onyx">
          {formatCurrency(summary.totalEarned)}
        </div>
        <div className="mt-space-1 text-small text-gray-mid">
          Across {summary.count} distribution{summary.count === 1 ? "" : "s"}
        </div>
      </div>

      {summary.items.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No commissions yet"
          description="Your earned servicing-fee income will appear here as borrower payments are distributed."
        />
      ) : (
        <div className="flex flex-col gap-space-3">
          {summary.items.map((item, i) => (
            <ListCard
              key={item.distributionId ?? `${item.loanId}-${item.periodStart}-${i}`}
              data-testid={`tps-commission-row-${i}`}
              title={`${item.loanNumber} — ${periodLabel(item.periodStart)}`}
              status={<StatusBadge tone={statusTone(item.status)}>{item.status}</StatusBadge>}
              meta={
                <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                  <span>{formatCurrency(item.amount)}</span>
                  {item.stripeTransferId && (
                    <>
                      <span aria-hidden>·</span>
                      <span>Paid out</span>
                    </>
                  )}
                </span>
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
